import { SubWithVarietyDto } from 'common/api/dto/get-subVarieties.dto';
import { WizardDropdownOption } from 'common/components/WizardControls/WizardDropdown';
import { Variety } from 'common/models';
import { useMemo, useState } from 'react';
import { Column } from 'react-table';
import {
  CenteredHeader,
  CenteredText,
} from '../components/Sub-views/RatesView/Tabs/styles';
import { VarietySelector } from '../components/Sub-views/VarietiesView/Tabs/VarietySelector';
import { createSubVarietyKey } from '../components/Sub-views/VarietiesView/utils/functions';

export const regVarName = 'regVar';
export const orgVarName = 'orgVar';

export type VarFormValue = {
  formValueKey: string;
  displayValue: string;
};

export type SubVarietiesTableRows = {
  code: string;
  conventionalVar: VarFormValue;
  organicVar: VarFormValue;
};

export const useSubVarietiesTableData: {
  (
    rows: SubWithVarietyDto[],
    control: unknown,
    options: {
      regVarOptions: WizardDropdownOption<Variety>[];
      orgVarOptions: WizardDropdownOption<Variety>[];
    },
    isLoadingVars: boolean,
    isSubmitting: boolean,
  ): {
    columns: Column<SubVarietiesTableRows>[];
    data: SubVarietiesTableRows[];
    isEditing: boolean;
    setIsEditing: (editing: boolean) => void;
  };
} = (rows, control, options, isLoadingVars, isSubmitting) => {
  const [isEditing, setIsEditing] = useState(false);

  const columns: Column<SubVarietiesTableRows>[] = useMemo(
    () => [
      {
        Header: () => <CenteredHeader>Sub-Variety</CenteredHeader>,
        accessor: 'code',
        width: '33%',
        Cell: ({ value }) => <CenteredText>{value}</CenteredText>,
      },
      {
        Header: () => <CenteredHeader>Conventional</CenteredHeader>,
        accessor: 'conventionalVar',
        width: '34%',
        Cell: ({ value }) => (
          <VarietySelector
            control={control}
            value={value}
            options={options.regVarOptions}
            isLoading={isLoadingVars}
            isEditing={isEditing}
            isSubmitting={isSubmitting}
          />
        ),
      },
      {
        Header: () => <CenteredHeader>Organic</CenteredHeader>,
        accessor: 'organicVar',
        width: '33%',
        Cell: ({ value }) => (
          <VarietySelector
            control={control}
            value={value}
            options={options.orgVarOptions}
            isLoading={isLoadingVars}
            isEditing={isEditing}
            isSubmitting={isSubmitting}
          />
        ),
      },
    ],
    [control, isEditing, isLoadingVars, isSubmitting, options],
  );

  const data: SubVarietiesTableRows[] = useMemo(
    () =>
      rows.map(({ id, subVarietyCode, conventionalVar, organicVar }) => ({
        code: subVarietyCode,
        conventionalVar: {
          formValueKey: createSubVarietyKey(id, regVarName),
          displayValue: conventionalVar?.varietyCode || '',
        },
        organicVar: {
          formValueKey: createSubVarietyKey(id, orgVarName),
          displayValue: organicVar?.varietyCode || '',
        },
      })),
    [rows],
  );

  return {
    columns,
    data,
    isEditing,
    setIsEditing,
  };
};
